import { ThemeProvider } from "@mui/material/styles";
import { Amplify } from "aws-amplify";
import { cognitoUserPoolsTokenProvider } from "aws-amplify/auth/cognito";
import { CookieStorage } from "aws-amplify/utils";
import { SignInForm } from "components/SignInForm";
import { PropsWithChildren } from "react";
import { getAuthFlowCookie } from "utils/authFlowCookie";

import { useEncampTheme } from "../theme";

// Configure Amplify BEFORE router initialization to handle OAuth callbacks
const isLocal = location.hostname.includes("localhost");
// We need to scope the cookie to a single domain to prevent bloated headers
// being sent to our support site resulting in a 400 error.
// If in the future we need to share cognito sessions across multiple applications
// the other applications will need to be added as subdomains of the main application,
// e.g. otherapp.ehs.encamp.com, and the cookie domain will need to be set to ".ehs.encamp.com".
const cookieStorageDomain = location.hostname;
const baseCognitoUrl = new URL(import.meta.env.VITE_COGNITO_BASE_URL);

// Determine which client ID to use based on the URL and cookie
const isIDPCallback = window.location.pathname === "/auth/callback";
const authFlow = getAuthFlowCookie();

// Configure Amplify based on the auth flow
const clientId =
  isIDPCallback || authFlow === "idp"
    ? import.meta.env.VITE_IDP_INITIATED_CLIENT_ID
    : import.meta.env.VITE_CLIENT_ID;

const redirectSignIn =
  isIDPCallback || authFlow === "idp" ? [`${location.origin}/auth/callback`] : [location.origin];

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_USER_POOL_ID,
      userPoolClientId: clientId,
      loginWith: {
        oauth: {
          domain: baseCognitoUrl.hostname,
          scopes: ["email", "openid", "profile"],
          responseType: "code",
          redirectSignIn,
          redirectSignOut: [location.origin],
        },
      },
    },
  },
});

// Configure secure cookie storage for Cognito tokens
// Note: AWS Amplify's CookieStorage is client-side, so HttpOnly cannot be set
// (HttpOnly requires server-side HTTP response headers). However, we configure
// all other security attributes to minimize risk:
// - secure: HTTPS-only (all environments except localhost)
// - sameSite: 'Strict' to prevent CSRF attacks
// - domain/path: Properly scoped to prevent bloated headers
cognitoUserPoolsTokenProvider.setKeyValueStorage(
  new CookieStorage({
    domain: cookieStorageDomain,
    path: "/",
    expires: 1, // Cookie lifetime (1 day); token lifetime configured in Cognito (15min)
    secure: !isLocal,
    sameSite: "strict",
  })
);

export function AuthProvider(props: PropsWithChildren<unknown>) {
  const encampTheme = useEncampTheme();
  return (
    <ThemeProvider theme={encampTheme}>
      <SignInForm>{props.children}</SignInForm>
    </ThemeProvider>
  );
}
